import { CommentStatus } from '@getaccept/lib-shared/src/contextual-comments/types/comment-status';
import type { Ref } from '@vue/composition-api';
import { unref, ref } from '@vue/composition-api';
import type { Comment, Reply } from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import type { MaybeRef } from '@vueuse/core';
import type { CommentEditorSelection } from '@getaccept/lib-shared/src/contextual-comments/types/comment-selection';
import type { CreateCommentDto } from '@getaccept/lib-shared/src/contextual-comments/types/create-comment-dto';
import type { UpdateCommentDto } from '@getaccept/lib-shared/src/contextual-comments/types/update-comment-dto';
import type { CreateReplyInput } from '@getaccept/lib-shared/src/contextual-comments/types/create-reply-input';
import { nanoid } from 'nanoid';
import { EditorHelper } from '@getaccept/editor-lib';
import type { CommentReadEvent } from '@getaccept/lib-shared/src/contextual-comments/types/comment-read-event';
import { CommentService } from '../../api/comments/services/comment.service';

export const useContextualComments = () => {
  const comments: Ref<Comment[]> = ref([]);
  const selectedCommentId = ref('');

  const loading = ref(false);
  const loaded = ref(false);
  const loadFailed = ref(false);

  const loadComments = async (
    statuses?: MaybeRef<CommentStatus[]>,
    limit?: MaybeRef<number>,
    offset?: MaybeRef<number>
  ) => {
    try {
      loading.value = true;
      loadFailed.value = false;
      comments.value = await CommentService.load(unref(statuses), unref(limit), unref(offset));
      loaded.value = true;
    } catch (e) {
      loadFailed.value = true;
    } finally {
      loading.value = false;
    }
  };

  const createComment = async (
    editorSelection: MaybeRef<CommentEditorSelection>,
    pageId: MaybeRef<string>
  ) => {
    const payload: CreateCommentDto = {
      text: '',
      pageId: unref(pageId),
      status: CommentStatus.Draft,
      editorSelection: unref(editorSelection),
    };

    try {
      const createdComment = await CommentService.create(payload);
      comments.value = [...comments.value, createdComment];

      return createdComment;
    } catch (e) {
      return null;
    }
  };

  const updateComment = async (payload: MaybeRef<Partial<UpdateCommentDto>>) => {
    const commentPartial = unref(payload);
    const staleComment = comments.value.find(
      (comment: Comment) => comment.id === commentPartial.commentId
    );

    comments.value = comments.value.map((comment: Comment) =>
      comment.id === commentPartial.commentId ? { ...comment, ...commentPartial } : comment
    );

    try {
      await CommentService.update(unref(commentPartial));
    } catch (e) {
      comments.value = comments.value.map(comment =>
        comment.id === staleComment.id ? staleComment : comment
      );
    }
  };

  const createReply = async (
    payload: MaybeRef<CreateReplyInput>,
    recipientId: MaybeRef<string>
  ) => {
    const createReplyInput = unref(payload);
    const tempId = nanoid(EditorHelper.NANO_ID_LENGTH);

    comments.value = comments.value.map((comment: Comment) =>
      comment.id === createReplyInput.commentId
        ? {
            ...comment,
            replies: [
              ...comment.replies,
              ({
                id: tempId,
                createdBy: { recipientId: unref(recipientId) },
                reads: [
                  { participant: { recipientId: unref(recipientId) }, timestamp: new Date() },
                ],
                ...createReplyInput,
              } as unknown) as Reply,
            ],
          }
        : comment
    );

    try {
      const createdReply = await CommentService.createReply(createReplyInput);
      comments.value = comments.value.map((comment: Comment) =>
        comment.id === createReplyInput.commentId
          ? {
              ...comment,
              replies: comment.replies.map(reply => (reply.id === tempId ? createdReply : reply)),
            }
          : comment
      );
    } catch (e) {
      comments.value = comments.value.map((comment: Comment) =>
        comment.id === createReplyInput.commentId
          ? {
              ...comment,
              replies: comment.replies.filter(reply => reply.id !== tempId),
            }
          : comment
      );
    }
  };

  const readComment = async (commentId: MaybeRef<string>, recipientId: MaybeRef<string>) => {
    comments.value = comments.value.map((comment: Comment) =>
      comment.id === unref(commentId)
        ? {
            ...comment,
            reads: [
              ...comment.reads,
              {
                participant: { userId: null, recipientId: unref(recipientId) },
                timestamp: Date.now(),
              },
            ],
          }
        : comment
    );

    try {
      await CommentService.readComment(unref(commentId));
    } catch (e) {
      loadComments();
    }
  };

  const readReply = async (
    replyId: MaybeRef<string>,
    commentId: MaybeRef<string>,
    recipientId: MaybeRef<string>
  ) => {
    comments.value = addReadToCommentReply(comments.value, unref(replyId), unref(commentId), {
      participant: { userId: null, recipientId: unref(recipientId) },
      timestamp: Date.now(),
    });

    await CommentService.readReply(unref(replyId), unref(commentId));
  };

  const addReadToCommentReply = (
    comments: Comment[],
    replyId: string,
    commentId: string,
    newRead: CommentReadEvent
  ) =>
    comments.map((comment: Comment) =>
      comment.id !== commentId
        ? comment
        : {
            ...comment,
            replies: comment.replies.map(reply =>
              reply.id !== replyId
                ? reply
                : {
                    ...reply,
                    reads: [...reply.reads, newRead],
                  }
            ),
          }
    );

  const selectComment = (commentId: MaybeRef<string>, recipientId: MaybeRef<string>) => {
    selectedCommentId.value = unref(commentId);

    const selectedComment = comments.value.find(comment => comment.id === selectedCommentId.value);

    if (!selectedComment) {
      return;
    }

    const isCommentUnread = !selectedComment.reads.find(
      ({ participant }) => participant.recipientId === unref(recipientId)
    );

    if (isCommentUnread) {
      readComment(selectedCommentId, unref(recipientId));
    }

    const unreadReplies = selectedComment.replies.filter(
      ({ reads }) =>
        !reads.find(({ participant }) => participant.recipientId === unref(recipientId))
    );

    unreadReplies.forEach(reply => readReply(reply.id, selectedComment.id, unref(recipientId)));
  };

  return {
    loading,
    loaded,
    loadFailed,
    comments,
    selectedCommentId,
    loadComments,
    createComment,
    updateComment,
    readComment,
    createReply,
    readReply,
    selectComment,
  };
};
